/**
 * services/NoteService.ts
 * Service for managing persisted notes
 *
 * Extends BaseService to provide CRUD operations for Note entities.
 * Notes are permanent records created when sessions are finalized (from NoteDraft),
 * or created directly for standalone 'analysis' type notes.
 */

import { BaseService } from './BaseService';
import { isTombstoned } from '@/utils/tombstone';
import type { Note, NoteType } from '@/types/schema/Note';

/**
 * Note types that have an owner entity.
 * Excludes 'analysis' which has no owner (ownerId is undefined).
 */
export type OwnedNoteType = Exclude<NoteType, 'analysis'>;

/**
 * Service for managing Note entities
 *
 * Provides CRUD operations plus owner-based lookup for cascade delete support,
 * and a separate method for retrieving standalone analysis notes.
 * Inherits add(), get(), getAll(), update(), softDelete(), hardDelete() from BaseService.
 */
export class NoteService extends BaseService<Note> {
  constructor() {
    super('notes');
  }

  /**
   * Get all notes for a specific owner
   *
   * Uses the compound index [noteType+ownerId] for efficient lookup.
   * Filters out tombstoned (soft-deleted) records.
   *
   * Note: This method excludes 'analysis' type at the type level since
   * analysis notes have no owner.
   *
   * @param noteType - The type of note (determines owner table, excludes 'analysis')
   * @param ownerId - The ID of the owning entity
   * @returns Promise resolving to array of notes for the owner
   */
  async getByOwner(noteType: OwnedNoteType, ownerId: string): Promise<Note[]> {
    try {
      const records = await this.table
        .where('[noteType+ownerId]')
        .equals([noteType, ownerId])
        .toArray();

      return records.filter(record => !isTombstoned(record));
    } catch (error) {
      console.error(
        `Failed to get notes for ${noteType}/${ownerId}:`,
        error
      );
      throw new Error('Failed to retrieve notes');
    }
  }

  /**
   * Get all standalone analysis notes
   *
   * Analysis notes have noteType 'analysis' and no owner (ownerId is undefined).
   * These are created outside of trading sessions for general market analysis.
   * Filters out tombstoned (soft-deleted) records.
   *
   * @returns Promise resolving to array of analysis notes
   */
  async getAnalysisNotes(): Promise<Note[]> {
    try {
      const allNotes = await this.getAll();
      return allNotes.filter(note => note.noteType === 'analysis');
    } catch (error) {
      console.error('Failed to get analysis notes:', error);
      throw new Error('Failed to retrieve analysis notes');
    }
  }
}

// Export singleton instance
export const noteService = new NoteService();
